/**
 * User Store
 * Jotai atoms for authentication and user state
 */

import { atom } from "jotai";
import type { AccountInfo } from "@azure/msal-browser";
import type { IUserState, IOrganizationSettings, IMenuItem } from "@/services";
import {
  ROLE_ADMIN,
  ROLE_MANAGER,
  ROLE_SALES,
  ROLE_EMPLOYEE,
} from "@/services";

/**
 * Core user state atom
 * Contains user data fetched from API after authentication
 */
export const userStateAtom = atom<IUserState | null>(null);

/**
 * MSAL account atom
 * Contains Azure AD account info from MSAL
 */
export const msalAccountAtom = atom<AccountInfo | null>(null);

/**
 * Authentication loading state
 */
export const authLoadingAtom = atom(true);

/**
 * Authentication error state
 */
export const authErrorAtom = atom<Error | null>(null);

/**
 * Pages accessible to the current user
 * Fetched from /organization/pages/accessible
 */
export const pagesAccessibleAtom = atom<IMenuItem[]>([]);

/**
 * User profile photo (base64 data URL)
 * Fetched from /user/photo
 */
export const userPhotoAtom = atom<string>("");

// ===== Derived Atoms =====

/**
 * Is user authenticated
 * True only if we have user state from API (backend authorization succeeded)
 * MSAL authentication alone is not sufficient - the API must also grant access
 */
export const isAuthenticatedAtom = atom((get) => get(userStateAtom) !== null);

/**
 * Organization settings
 */
export const organizationSettingsAtom = atom<IOrganizationSettings>(
  (get) =>
    get(userStateAtom)?.organization_settings ?? {
      hours_in_day: 8,
      disable_fravaer: false,
      include_holidays: true,
      include_weekends: false,
    },
);

/**
 * Hours in day setting
 */
export const hoursInDayAtom = atom(
  (get) => get(organizationSettingsAtom).hours_in_day,
);

/**
 * Fravaer (absence) disabled setting
 */
export const disableFravaerAtom = atom(
  (get) => get(organizationSettingsAtom).disable_fravaer,
);

/**
 * User role level
 */
export const userRoleLevelAtom = atom(
  (get) => get(userStateAtom)?.role_level ?? ROLE_EMPLOYEE,
);

/**
 * Role check helpers
 */
export const isAdminAtom = atom((get) => get(userRoleLevelAtom) === ROLE_ADMIN);
export const isManagerAtom = atom(
  (get) => get(userRoleLevelAtom) <= ROLE_MANAGER,
);
export const isSalesAtom = atom((get) => get(userRoleLevelAtom) <= ROLE_SALES);

/**
 * User display name
 */
export const userDisplayNameAtom = atom((get) => {
  const user = get(userStateAtom);
  if (!user) return "";
  return user.name || user.email;
});

/**
 * User email
 */
export const userEmailAtom = atom((get) => get(userStateAtom)?.email ?? "");

/**
 * Check if user has access to a specific route
 * Usage: const hasAccess = useAtomValue(hasAccessToAtom)('/booking')
 * Fallback: When pagesAccessible is empty (API failed), grant access to /booking
 */
export const hasAccessToAtom = atom((get) => {
  const pages = get(pagesAccessibleAtom);
  return (route: string): boolean => {
    // If pages list is empty (API failed), grant access to booking page as fallback
    if (pages.length === 0) {
      return route === "/booking";
    }
    return pages.some((page) => page.route === route);
  };
});

// Re-export role constants
export { ROLE_ADMIN, ROLE_MANAGER, ROLE_SALES, ROLE_EMPLOYEE };
